import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { z } from 'zod'

import {
  emptyPreferences,
  SOFTWARE_IDS,
  STORAGE_KEY,
  type Preferences,
  type Session,
  type SoftwareCode,
  type StoredState,
  type Workspace,
  workspaceOrNull,
} from './session'

// Lenient on purpose: the stored JSON is ours, but an older or hand-edited value must not break the boot.
const storedSchema = z.object({
  version: z.literal(1),
  session: z
    .object({
      token: z.string().min(1),
      userType: z.enum(['Client', 'Staff', 'Freelancer', 'User']),
      profile: z.object({ Id: z.number() }).passthrough(),
    })
    .passthrough()
    .nullable(),
  prefs: z
    .object({
      locale: z.string().nullable().catch(null),
      columns: z.record(z.unknown()).catch({}),
      dontRemind: z.record(z.boolean()).catch({}),
      aiMode: z.record(z.boolean()).optional().catch(undefined),
    })
    .catch(emptyPreferences()),
})

/**
 * The stored preferences plus the AI mode (docs/ai-mode, AI-1). Optional, so a state stored before it — and the e2e
 * seeds built from `emptyPreferences()` — stay valid; `src/app/stores/session.ts` keeps the shape the fixtures share.
 */
export interface ViewerPreferences extends Preferences {
  /** Users (`viewerKey()`) who turned AI mode on with the navbar switch: their home is `/ai` (beta flag on). */
  aiMode?: Record<string, boolean>
}

type ViewerState = Omit<StoredState, 'prefs'> & { prefs: ViewerPreferences }

/**
 * Whose preference it is: the preferences are per viewer (browser) and survive logout, so a per-user choice is keyed
 * by the user type and the account id (a Client and a Staff account may share an id).
 */
export function viewerKey(session: Pick<Session, 'userType' | 'profile'>): string {
  return `${session.userType}:${session.profile.Id}`
}

function readStorage(): ViewerState | null {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = storedSchema.safeParse(JSON.parse(raw))
    return parsed.success ? (parsed.data as ViewerState) : null
  } catch {
    return null
  }
}

function writeStorage(state: ViewerState): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Storage full or blocked (private mode): the session lives in memory for this tab only.
  }
}

/**
 * Session and per-viewer preferences. The **only** module that touches `localStorage` (ARCHITECTURE.md §9): one JSON
 * key, `skileo.session`. Logout clears `session` and keeps `prefs` (legacy kept `locale`, column preferences and
 * "don't remind" flags across a logout; the AI mode per user rides along — docs/ai-mode/wp/AI-1.md).
 */
export const useAuthStore = defineStore('auth', () => {
  const session = ref<Session | null>(null)
  const prefs = ref<ViewerPreferences>(emptyPreferences())

  function persist(): void {
    writeStorage({ version: 1, session: session.value, prefs: prefs.value })
  }

  /** Boot step "session restore" (wp/shell §4.12): reads the stored state once. */
  function restore(): void {
    const stored = readStorage()
    session.value = stored?.session ?? null
    prefs.value = stored?.prefs ?? emptyPreferences()
  }

  function setSession(value: Session): void {
    session.value = value
    persist()
  }

  function clearSession(): void {
    session.value = null
    persist()
  }

  function update(patch: Partial<Session>): void {
    if (!session.value) return
    session.value = { ...session.value, ...patch }
    persist()
  }

  const isLoggedIn = computed(() => Boolean(session.value?.token))
  const token = computed(() => session.value?.token ?? null)
  const userType = computed(() => session.value?.userType ?? null)
  const currentSoftware = computed(() => session.value?.currentSoftware ?? null)
  const currentWorkspace = computed(() => session.value?.currentWorkspace ?? null)
  /** `software_id` sent with requests; `null` without a session or software. */
  const softwareId = computed(() => (currentSoftware.value ? SOFTWARE_IDS[currentSoftware.value] : null))
  /** `company_id` sent with requests; `null` without a workspace (OPEN-13 default: omit it). */
  const companyId = computed(() => currentWorkspace.value?.Id ?? null)

  function setCurrentSoftware(code: SoftwareCode): void {
    update({ currentSoftware: code })
  }

  function setCurrentWorkspace(workspace: Workspace | null): void {
    update({ currentWorkspace: workspaceOrNull(workspace) })
  }

  function setLocale(locale: string | null): void {
    prefs.value = { ...prefs.value, locale }
    persist()
  }

  function getColumnPreferences<T = unknown>(list: string): T | undefined {
    return prefs.value.columns[list] as T | undefined
  }

  function setColumnPreferences(list: string, value: unknown): void {
    prefs.value = { ...prefs.value, columns: { ...prefs.value.columns, [list]: value } }
    persist()
  }

  function isDontRemind(name: string): boolean {
    return prefs.value.dontRemind[name] === true
  }

  function setDontRemind(name: string, value: boolean): void {
    prefs.value = { ...prefs.value, dontRemind: { ...prefs.value.dontRemind, [name]: value } }
    persist()
  }

  /** AI mode remembered for the user of `target` (the current session by default) — the navbar switch's preference. */
  function isAiMode(target: Session | null = session.value): boolean {
    return target ? prefs.value.aiMode?.[viewerKey(target)] === true : false
  }

  /** Remembers (or forgets) AI mode for the signed-in user; without a session there is no one to remember it for. */
  function setAiMode(on: boolean): void {
    if (!session.value) return
    const key = viewerKey(session.value)
    const others = Object.fromEntries(Object.entries(prefs.value.aiMode ?? {}).filter(([entry]) => entry !== key))
    prefs.value = { ...prefs.value, aiMode: on ? { ...others, [key]: true } : others }
    persist()
  }

  return {
    session,
    prefs,
    restore,
    setSession,
    clearSession,
    update,
    isLoggedIn,
    token,
    userType,
    currentSoftware,
    currentWorkspace,
    softwareId,
    companyId,
    setCurrentSoftware,
    setCurrentWorkspace,
    setLocale,
    getColumnPreferences,
    setColumnPreferences,
    isDontRemind,
    setDontRemind,
    isAiMode,
    setAiMode,
  }
})
